import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { LoginService } from '../service/login.service';
import { ResetPasswordLinkStateService } from '../service/reset-password-link-state.service';
import { UserService } from '../service/user.service';

/**
* Verifies a reset-password link token, initializes reset state, and redirects to an expired-link page when the token is invalid or expired.
* @example
* resetPasswordLinkGuard(route)
* true
* @param {ActivatedRouteSnapshot} route - Route snapshot containing the reset token in the query parameters.
* @returns {boolean | UrlTree | Observable<boolean | UrlTree>} Returns true when no token is provided or the token is valid; otherwise returns a redirect UrlTree to the expired-link page.
**/
export const resetPasswordLinkGuard: CanActivateFn = (route) => {
  const resetToken = route.queryParamMap.get('data') || '';
  const router = inject(Router);
  const loginService = inject(LoginService);
  const userService = inject(UserService);
  const resetPasswordLinkState = inject(ResetPasswordLinkStateService);

  if (!resetToken) {
    resetPasswordLinkState.clearState();
    return true;
  }

  const redirectToExpiredLink = (token: string, email: string = '') =>
    router.createUrlTree(['/linkedExpired'], {
      queryParams: {
        data: token,
        email
      }
    });

  return loginService.verifyLinkExpiry(encodeURIComponent(resetToken)).pipe(
    switchMap((response) => {
      if (response.status !== 200 || !response.body?.success) {
        return of(
          redirectToExpiredLink(
            response.body?.token || resetToken,
            response.body?.user_email || ''
          )
        );
      }

      const validatedToken = response.body.token || resetToken;
      const userEmail = response.body.user_email || '';
      const userId = response.body.user_id || 0;

      return loginService.getIdentifiersWithToken(validatedToken).pipe(
        map((identifierResponse) => {
          const verifier = identifierResponse.body?.result?.verifier;
          const identifier = identifierResponse.body?.result?.identifier;

          if (!verifier) {
            return redirectToExpiredLink(validatedToken, userEmail);
          }

          userService.setVerifier(atob(verifier));

          if (identifier) {
            userService.setIdentifier(atob(identifier));
          }

          resetPasswordLinkState.setState({
            userId,
            userEmail,
            resetToken: validatedToken
          });

          return true;
        }),
        catchError(() => of(redirectToExpiredLink(validatedToken, userEmail)))
      );
    }),
    catchError((error) =>
      of(
        redirectToExpiredLink(
          error?.error?.token || resetToken,
          error?.error?.user_email || ''
        )
      )
    )
  );
};